/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, defineMessage} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {Box, Container} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerBaskets,
    useCustomerId,
    useCustomerType,
    useShopperBasketsMutation,
    useCustomer
} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {useForm} from 'react-hook-form'
import {useRouteMatch} from 'react-router'
import {useLocation} from 'react-router-dom'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import LoginForm from '../../components/login'
import useCustomerSearch from '../../hooks/useCustomerSearch'
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index'
import {
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_LOGIN_LANDING_PATH,
    PASSWORDLESS_ERROR_MESSAGES,
    USER_NOT_FOUND_ERROR
} from '@salesforce/retail-react-app/app/constants'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {parseApiError} from '../../components/forms/parseApiError/parseApiError'
import {useLoginContext} from '../../components/login/loginContext'
import {pxToRem} from '../../utills/pxToRem'
import {pushAnalyticsEvent} from '../../../app/utills/analytics'

const LOGIN_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'Incorrect username or password, please try again.',
    id: 'login_page.error.incorrect_username_or_password'
})

const LOGIN_VIEW = 'login'
const EMAIL_VIEW = 'email'

const Login = ({initialView = LOGIN_VIEW}) => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const form = useForm()
    const location = useLocation()
    const history = useHistory()
    const queryParams = new URLSearchParams(location.search)
    const {path} = useRouteMatch()
    const einstein = useEinstein()
    const {isRegistered, customerType} = useCustomerType()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const customerId = useCustomerId()
    const customer = useCustomer({parameters: {customerId}}, {enabled: !!customerId && !isServer})
    const [hasSearchedCustomer, setHasSearchedCustomer] = useState(false)
    const [emailCustomerSearch, setEmailCustomerSearch] = useState(false)
    const [inStoreCustomerEmail, setInStoreCustomerEmail] = useState(null)
    const loginPasswordless = useAuthHelper(AuthHelpers.LoginPasswordlessUser)
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const {passwordless = {}, social = {}} = getConfig().app.login || {}
    const isPasswordlessEnabled = !!passwordless?.enabled
    const isSocialEnabled = !!social?.enabled
    const idps = social?.idps
    const {setEmail} = useLoginContext()
    const prevAuthType = usePrevious(customerType)
    const {data: baskets, isSuccess: isSuccessCustomerBaskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const [currentView, setCurrentView] = useState(initialView)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState('')
    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)
    const [redirectPath, setRedirectPath] = useState('')

    const {data: searchCustomData, isLoading: isCustomerSearchLoading} = useCustomerSearch(
        emailCustomerSearch,
        !hasSearchedCustomer
    )

    const handleMergeBasket = () => {
        const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
        const hasGiftcertificate = baskets?.baskets?.[0]?.giftCertificateItems?.length > 0
        // we only want to merge basket when the user is logged in as a recurring user
        // only recurring users trigger the login mutation, new user triggers register mutation
        // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
        // if you change logic here, also change it in login page
        const shouldMergeBasket = (hasBasketItem || hasGiftcertificate) && prevAuthType === 'guest'
        if (shouldMergeBasket) {
            try {
                mergeBasket.mutate({
                    headers: {
                        // This is not required since the request has no body
                        // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                        'Content-Type': 'application/json'
                    },
                    parameters: {
                        createDestinationBasket: true,
                        productItemMergeMode: 'sum_quantities'
                    }
                })
            } catch (e) {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(API_ERROR_MESSAGE)
                })
            }
        }
    }

    const submitForm = async (data) => {
        form.clearErrors()

        const handlePasswordlessLogin = async (email) => {
            try {
                await authorizePasswordlessLogin.mutateAsync({userid: email})
                setCurrentView(EMAIL_VIEW)
            } catch (error) {
                const message = USER_NOT_FOUND_ERROR.test(error.message)
                    ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                    : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                    ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                    : formatMessage(API_ERROR_MESSAGE)
                form.setError('global', {type: 'manual', message})
            }
        }

        return {
            login: async (data) => {
                if (loginType === LOGIN_TYPES.PASSWORD) {
                    try {
                        await login.mutateAsync({username: data.email, password: data.password})
                        if (typeof window !== 'undefined') {
                            localStorage.removeItem('passwordResetEmail')
                        }
                        handleMergeBasket()
                        if (typeof window !== 'undefined' && window.dataLayer) {
                            await pushAnalyticsEvent({
                                eventName: 'loginToAccount',
                                userId: customer?.data?.customerId || customerId || '',
                                email: data.email,
                                registered: customer?.isRegistered ?? true,
                                phone: customer?.data?.phoneMobile || '',
                                includePlainEmail: true
                            })
                        }
                    } catch (error) {
                        const errorBody = await parseApiError(error, form)
                        if (/Unauthorized/i.test(errorBody?.status_code)) {
                            form.setError('global', {
                                type: 'manual',
                                message: 'Password incorrect, please try again. '
                            })
                        } else {
                            form.setError('global', {type: 'manual', message: errorBody?.message})
                        }
                    }
                } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                    setPasswordlessLoginEmail(data.email)
                    await handlePasswordlessLogin(data.email)
                    // Save email to localStorage for reset password prefill
                    if (typeof window !== 'undefined' && data.email) {
                        localStorage.setItem('passwordResetEmail', data.email)
                    }
                }
            },
            email: async () => {
                await handlePasswordlessLogin(passwordlessLoginEmail)
            }
        }[currentView](data)
    }

    const handleEmailSubmit = async () => {
        const emailValue = form.getValues('email')
        setEmailCustomerSearch(emailValue)
        const valid = await form.trigger('email')
        if (valid) {
            if (emailValue === inStoreCustomerEmail) {
                history.push('/verify-email', {
                    email: emailValue
                })
                return
            }
            setHasSearchedCustomer(true)

            // set email to login context to pass it to register page
            setEmail(form.getValues('email'))

            // Save email to localStorage for reset password prefill
            if (typeof window !== 'undefined' && form.getValues('email')) {
                localStorage.setItem('passwordResetEmail', form.getValues('email'))
            }
        } else {
            form.setFocus('email')
        }
    }

    const handleEditEmailClick = () => {
        form.resetField('password')
        setHasSearchedCustomer(false)
        setEmailCustomerSearch(false)
    }

    useEffect(() => {
        if (hasSearchedCustomer && searchCustomData && !searchCustomData?.customerExists) {
            navigate('/register')
        }
    }, [hasSearchedCustomer, searchCustomData])

    // Handles passwordless login by retrieving the 'token' from the query parameters and
    // executing a passwordless login attempt using the token. The process waits for the
    // customer baskets to be loaded to guarantee proper basket merging.
    useEffect(() => {
        if (path === PASSWORDLESS_LOGIN_LANDING_PATH && isSuccessCustomerBaskets) {
            const token = decodeURIComponent(queryParams.get('token'))
            if (queryParams.get('redirect_url')) {
                setRedirectPath(decodeURIComponent(queryParams.get('redirect_url')))
            } else {
                setRedirectPath('')
            }

            const passwordlessLogin = async () => {
                try {
                    await loginPasswordless.mutateAsync({pwdlessLoginToken: token})
                } catch (e) {
                    const errorData = await e.response?.json()
                    const message = INVALID_TOKEN_ERROR.test(errorData.message)
                        ? formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                        : formatMessage(API_ERROR_MESSAGE)
                    form.setError('global', {type: 'manual', message})
                }
            }
            passwordlessLogin()
        }
    }, [path, isSuccessCustomerBaskets])

    // If customer is registered push to account page and merge the basket
    useEffect(() => {
        if (isRegistered) {
            handleMergeBasket()
            if (typeof window !== 'undefined') {
                localStorage.removeItem('passwordResetEmail')
            }
            const redirectTo = redirectPath ? redirectPath : '/account'
            navigate(redirectTo)
        }
    }, [isRegistered, redirectPath])

    useEffect(() => {
        if (path === '/login' && !form.formState.isSubmitSuccessful) {
            const token = decodeURIComponent(queryParams.get('token'))
            const regType = decodeURIComponent(queryParams.get('regType'))
            if (regType === 'instore') {
                try {
                    const storeCustomerEmail = atob(token)
                    setInStoreCustomerEmail(storeCustomerEmail)
                    form.setValue('email', storeCustomerEmail, {shouldValidate: true})
                } catch (err) {
                    console.error(err, 'Invalid store customer email')
                    setInStoreCustomerEmail(null)
                }
            }
        }
    }, [path, form.formState.isSubmitSuccessful])

    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(location.pathname)
    }, [])

    return (
        <Box data-testid="login-page" bg="gray.50">
            <Seo title="Sign in" description="Customer sign in" />
            <Container
                paddingY={{base: pxToRem(30), lg: pxToRem(40)}}
                paddingX={{base: pxToRem(20), lg: pxToRem(25)}}
                width={['100%', '461px']}
                bg="white"
                marginTop={8}
                marginBottom={8}
                borderRadius="base"
            >
                {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                    <LoginForm
                        form={form}
                        hideEmail={searchCustomData?.customerExists}
                        hidePassword={!searchCustomData?.customerExists}
                        isCustomerSearchLoading={isCustomerSearchLoading}
                        submitForm={submitForm}
                        handleEmailSubmit={handleEmailSubmit}
                        handleEditEmailClick={handleEditEmailClick}
                        clickCreateAccount={() => navigate('/registration')}
                        handlePasswordlessLoginClick={() => {
                            setLoginType(LOGIN_TYPES.PASSWORDLESS)
                        }}
                        handleForgotPasswordClick={() => navigate('/reset-password')}
                        isPasswordlessEnabled={isPasswordlessEnabled}
                        isSocialEnabled={isSocialEnabled}
                        idps={idps}
                        setLoginType={setLoginType}
                    />
                )}
                {form.formState.isSubmitSuccessful && currentView === EMAIL_VIEW && (
                    <PasswordlessEmailConfirmation
                        form={form}
                        submitForm={submitForm}
                        email={passwordlessLoginEmail}
                    />
                )}
            </Container>
        </Box>
    )
}

Login.getTemplateName = () => 'login'

Login.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, EMAIL_VIEW]),
    match: PropTypes.object
}

export default Login
